import { useEffect, useState } from "react";
import { api, formatTL } from "@/lib/api";
import { FIN_TYPES, num } from "@/lib/docOptions";
import { DocDialog } from "@/components/docs/DocDialog";
import { F, Section, PartyFields } from "@/components/docs/Fields";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = () => ({
  title: "", portfolio_id: null, registry_office: "", appointment_at: "", city: "", district: "", neighborhood: "", ada: "", parsel: "",
  seller: {}, buyer: {}, sale_price: "", is_proxy: false, proxy_notary: "", proxy_yevmiye: "", proxy_date: "",
  has_mortgage: false, mortgage_note: "", financing: "CASH", financing_institution: "", loan_amount: "", notes: "",
});

const normalize = (f) => ({ ...f, sale_price: num(f.sale_price), loan_amount: num(f.loan_amount) });

const Fees = ({ price }) => {
  const [fees, setFees] = useState(null);
  useEffect(() => { api.get("/deed-fees").then((r) => setFees(r.data)); }, []);
  if (!fees) return null;
  const harc = (Number(price) || 0) * fees.harc_per_mille / 1000;
  return (
    <div className="grid grid-cols-3 gap-2 text-sm rounded-lg bg-slate-50 p-3" data-testid="deed-fees-preview">
      <div>Tapu harcı (binde {fees.harc_per_mille})<div className="font-bold" data-testid="deed-harc">{formatTL(harc)}</div></div>
      <div>Döner sermaye<div className="font-bold" data-testid="deed-doner">{formatTL(fees.doner_sermaye)}</div></div>
      <div>Toplam masraf<div className="font-bold text-emerald-700">{formatTL(harc + fees.doner_sermaye)}</div></div>
    </div>
  );
};

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm font-medium"><Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />{label}</label>
);

export const DeedForm = (props) => (
  <DocDialog {...props} kind="deeds" heading="Tapu takip" empty={empty} normalize={normalize}>
    {(f, set) => (
      <>
        <Section title="Tapu bilgileri">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <F label="Tapu müdürlüğü" className="col-span-2" testid="deed-registry" value={f.registry_office} onChange={(v) => set({ registry_office: v })} />
            <F label="Randevu tarihi/saati" type="datetime-local" className="col-span-2" testid="deed-appointment" value={f.appointment_at} onChange={(v) => set({ appointment_at: v })} />
            <F label="İl" value={f.city} onChange={(v) => set({ city: v })} />
            <F label="İlçe" value={f.district} onChange={(v) => set({ district: v })} />
            <F label="Mahalle" value={f.neighborhood} onChange={(v) => set({ neighborhood: v })} />
            <F label="Ada" testid="deed-ada" value={f.ada} onChange={(v) => set({ ada: v })} />
            <F label="Parsel" testid="deed-parsel" value={f.parsel} onChange={(v) => set({ parsel: v })} />
          </div>
        </Section>
        <Section title="Satıcı"><PartyFields prefix="deed-seller" value={f.seller} onChange={(v) => set({ seller: v })} /></Section>
        <Section title="Alıcı"><PartyFields prefix="deed-buyer" value={f.buyer} onChange={(v) => set({ buyer: v })} /></Section>
        <Section title="Bedel ve masraflar">
          <F label="Satış bedeli (TL)" type="number" testid="deed-price" value={f.sale_price} onChange={(v) => set({ sale_price: v })} />
          <Fees price={f.sale_price} />
        </Section>
        <Section title="İşlem detayları">
          <Toggle label="Vekaletli işlem" testid="deed-proxy-switch" checked={f.is_proxy} onChange={(v) => set({ is_proxy: v })} />
          {f.is_proxy && (
            <div className="grid grid-cols-3 gap-2">
              <F label="Noterlik" testid="deed-proxy-notary" value={f.proxy_notary} onChange={(v) => set({ proxy_notary: v })} />
              <F label="Yevmiye no" testid="deed-proxy-yevmiye" value={f.proxy_yevmiye} onChange={(v) => set({ proxy_yevmiye: v })} />
              <F label="Vekalet tarihi" type="date" value={f.proxy_date} onChange={(v) => set({ proxy_date: v })} />
            </div>
          )}
          <Toggle label="İpotekli" testid="deed-mortgage-switch" checked={f.has_mortgage} onChange={(v) => set({ has_mortgage: v })} />
          {f.has_mortgage && <F label="İpotek bilgisi (banka, tutar vb.)" testid="deed-mortgage-note" value={f.mortgage_note} onChange={(v) => set({ mortgage_note: v })} />}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Ödeme / finansman</Label>
              <Select value={f.financing} onValueChange={(v) => set({ financing: v })}>
                <SelectTrigger data-testid="deed-financing"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FIN_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {f.financing !== "CASH" && <>
              <F label={f.financing === "BANK_LOAN" ? "Banka" : "Finansman şirketi"} testid="deed-institution" value={f.financing_institution} onChange={(v) => set({ financing_institution: v })} />
              <F label="Kredi tutarı (TL)" type="number" testid="deed-loan" value={f.loan_amount} onChange={(v) => set({ loan_amount: v })} />
            </>}
          </div>
          <p className="text-[11px] text-slate-500">Vekalet ve ipotek evrakını kaydettikten sonra listeden yükleyebilirsiniz.</p>
        </Section>
        <F label="Açıklama" area value={f.notes} onChange={(v) => set({ notes: v })} />
      </>
    )}
  </DocDialog>
);
